import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ERROR_DEFAULT_MESSAGE,
  PRODUCTION_HOSTS,
  ROUTE_PATHS,
  SESSION_STORAGE_KEYS,
  SESSION_STORAGE_SUFFIX,
  SESSION_STORAGE_VALUES,
} from '@app/constants';
import { ServiceType } from '@app/interface/service';
import { AuthService } from '@app/services/auth.service';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

export interface ResponseError {
  headers: Headers;
  status: number;
  statusText: string;
  url: string;
  ok: boolean;
  name: string;
  message: string;
  error: MessageErrorTypes;
}

export interface MessageErrorTypes {
  exceptionName: string;
  userError: string;
  error?: string;
  message?: string;
}

export interface Headers {
  normalizedNames: object;
  lazyUpdate: null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ResponseType = any;
export type ResponseErrorType = ResponseType;

export type doGetResponseType<T = ResponseType> = {
  success: boolean;
  error?: ResponseErrorType;
  data?: T;
  status?: number;
};

export type RequestParams = Record<
  string,
  string | number | boolean | Array<string | number | boolean>
>;

@Injectable({
  providedIn: 'root',
})
export class GatewayService {
  constructor(
    private readonly http: HttpClient,
    private readonly auth: AuthService
  ) {}

  getError(error: ResponseErrorType) {
    if (error && !!error.error && !!error.error.userError) {
      return JSON.stringify(error.error.userError);
    }

    if (error.error) {
      if (
        typeof error.error === 'object' &&
        error.error !== null &&
        'message' in error.error
      ) {
        return error.error.message;
      }
      return JSON.stringify(error.error.errorMessage);
    }
    return ERROR_DEFAULT_MESSAGE;
  }

  getEndPoint(url: ServiceType) {
    const urls: Record<string, string> = {
      [ServiceType.Assessments]: environment.webServiceEndpoint,
      [ServiceType.AssessmentsPA]: environment.webServiceAssessment,
      [ServiceType.AssessmentService]: environment.webServiceAssessment,
      [ServiceType.AdministrationService]: environment.webServiceChapter,
      [ServiceType.SchedulingService]: environment.webServiceScheduling,
      [ServiceType.MapRatingService]: environment.webServiceMapRating,
      [ServiceType.CommentService]: environment.webComments,
      [ServiceType.AttachmentService]: environment.webAttachments,
      [ServiceType.MapConfigurationService]:
        environment.webServiceMapConfiguration,
      [ServiceType.SkillsetService]: environment.webServiceSkillset,
    };
    return url in urls ? urls[url] : environment.GrowthEndpoint;
  }

  getEndAchievements(url: ServiceType) {
    return url === ServiceType.Assessments
      ? environment.webServiceAchievements
      : environment.GrowthEndpoint;
  }

  wraprequest<T>(execution: Observable<T>): Promise<doGetResponseType<T>> {
    return new Promise<doGetResponseType<T>>((resolve, reject) => {
      let response: T;

      const responseObserver = {
        next: (_response: T) => {
          response = _response;
        },
        complete: () => {
          resolve({ success: true, data: response });
        },
        error: (error: ResponseError) => {
          const context = this.getErrorContext(error);
          if (context.isPragmaticNotFound) {
            const handled = this.handlePragmaticNotFound(context, resolve);
            if (handled) return;
          }
          reject({
            success: false,
            error: this.getError(error),
            status: error.status,
          } as unknown as Error);
        },
      };

      execution.subscribe(responseObserver);
    });
  }
  private getErrorContext(error: ResponseError) {
    const currentPath = window.location.pathname;
    const currentHost = window.location.hostname;
    const errorUrl = error?.url || '';

    return {
      isPragmaticNotFound:
        error?.error?.exceptionName === 'PragmaticNotFoundException',
      isNonProdEnvironment: !this.isProductionHost(currentHost),
      isLoginContext: currentPath.includes(ROUTE_PATHS.LOGIN),
      isUserLogged: !!sessionStorage.getItem(SESSION_STORAGE_KEYS.IS_LOGIN),
      isProfileEndpoint: errorUrl.includes(ROUTE_PATHS.PRAGMATIC_PROFILE),
      currentPath,
      currentHost,
      errorUrl,
    };
  }

  private handlePragmaticNotFound<T>(
    context: ReturnType<typeof this.getErrorContext>,
    resolve: (value: doGetResponseType<T>) => void
  ): boolean {
    const {
      isLoginContext,
      isNonProdEnvironment,
      isProfileEndpoint,
      isUserLogged,
    } = context;

    if (isLoginContext) {
      const flag = isNonProdEnvironment
        ? SESSION_STORAGE_KEYS.WRONG_ENVIRONMENT_DETECTED
        : SESSION_STORAGE_KEYS.USER_NOT_FOUND_ERROR;
      sessionStorage.setItem(flag, SESSION_STORAGE_VALUES.TRUE);
      sessionStorage.setItem(
        `${flag}${SESSION_STORAGE_SUFFIX.TIMESTAMP}`,
        new Date().toISOString()
      );
      this.auth.signOut();
      return true;
    }
    if (isProfileEndpoint && (isUserLogged || isNonProdEnvironment)) {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.WRONG_ENVIRONMENT_DETECTED,
        SESSION_STORAGE_VALUES.TRUE
      );
      sessionStorage.setItem(
        `${SESSION_STORAGE_KEYS.WRONG_ENVIRONMENT_DETECTED}${SESSION_STORAGE_SUFFIX.TIMESTAMP}`,
        new Date().toISOString()
      );
      this.auth.signOut();
      return true;
    }
    resolve({
      success: false,
      data: undefined as unknown as T,
    } as unknown as doGetResponseType<T>);

    return true;
  }

  private isProductionHost(hostname: string): boolean {
    const isProd = PRODUCTION_HOSTS.includes(hostname);
    return isProd;
  }

  doGet<T>(
    pathinfo: string,
    _params: RequestParams = {},
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const options = { params: new HttpParams().appendAll(_params) };
    const url = this.getEndPoint(service) + pathinfo;
    const request = this.http.get<T>(url, options);
    return this.wraprequest<T>(request);
  }

  doGetAchievements<T>(
    pathinfo: string,
    _params: RequestParams = {},
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const options = { params: new HttpParams().appendAll(_params) };
    const url = this.getEndAchievements(service) + pathinfo;
    const request = this.http.get<T>(url, options);
    return this.wraprequest<T>(request);
  }

  doPost<T>(
    pathinfo: string,
    _payLoad: object = {},
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const url = this.getEndPoint(service) + pathinfo;
    const request = this.http.post<T>(url, _payLoad);
    return this.wraprequest<T>(request);
  }

  doPostAchievements<T>(
    pathinfo: string,
    _payLoad: object = {},
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const url = this.getEndAchievements(service) + pathinfo;
    const request = this.http.post<T>(url, _payLoad);
    return this.wraprequest<T>(request);
  }

  doPut<T>(
    pathinfo: string,
    _payLoad: object = {},
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const url = this.getEndPoint(service) + pathinfo;
    const request = this.http.put<T>(url, _payLoad);
    return this.wraprequest<T>(request);
  }

  doPutAchievements<T>(
    pathinfo: string,
    _payLoad: object = {},
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const url = this.getEndAchievements(service) + pathinfo;
    const request = this.http.put<T>(url, _payLoad);
    return this.wraprequest<T>(request);
  }

  doPatch<T>(
    pathinfo: string,
    _payLoad: object = {},
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const url = this.getEndPoint(service) + pathinfo;
    const request = this.http.patch<T>(url, _payLoad);
    return this.wraprequest<T>(request);
  }

  doPatchAchivements<T>(
    pathinfo: string,
    _payLoad: object = {},
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const url = this.getEndAchievements(service) + pathinfo;
    const request = this.http.patch<T>(url, _payLoad);
    return this.wraprequest<T>(request);
  }

  doDelete<T>(
    pathinfo: string,
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const url = this.getEndPoint(service) + pathinfo;
    const request = this.http.delete<T>(url);
    return this.wraprequest<T>(request);
  }

  doDeleteBehabior<T>(
    pathinfo: string,
    params: RequestParams = {},
    service: ServiceType = ServiceType.Assessments
  ): Promise<doGetResponseType<T>> {
    const options = { params: new HttpParams().appendAll(params) };
    const url = this.getEndPoint(service) + pathinfo;
    const request = this.http.delete<T>(url, options);
    return this.wraprequest<T>(request);
  }
}
